import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const VerifyCommissionStateDetailsReport = ({
  month,
  year,
  allotMonth,
  allotYear,
  distMonthName,
  alotMonthName,
  distCode,
}) => {
  const [submitMessage, setSubmitMessage] = useState("");
  const [loading, setLoading] = useState([]);
  const [verifyStateAbsCommission, setVerifyStateAbsCommission] = useState([]);
  const [error, setError] = useState(null);
  const [commissionData, setCommissionData] = useState([]);

  useEffect(() => {
    const fetchVerifyStateCommissionDetails = async () => {
      try {
        setSubmitMessage("");
        setLoading(true);
        const verifyCommissionResponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/verify-state-commission?month=${month}&year=${year}&alotMonth=${allotMonth}&alotYear=${allotYear}&distCode=${distCode}`
        );
        if (!verifyCommissionResponse.ok) {
          throw new Error("Failed to fetch state commission abstract");
        }
        const data = await verifyCommissionResponse.json();
        setVerifyStateAbsCommission(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchVerifyStateCommissionDetails();
  }, [month, year, allotMonth, allotYear, distCode]);

  useEffect(() => {
    const fetCommissionCost = async () => {
      try {
        setLoading(true);
        const commissioncostresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-cost`
        );

        if (!commissioncostresponse.ok) {
          throw new Error("Failed to fetch State commission data");
        }

        const data = await commissioncostresponse.json();
        setCommissionData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetCommissionCost();
  }, []);

  const totals = verifyStateAbsCommission.reduce(
    (acc, item) => {
      acc.riceQty += Number(item.riceQty) || 0;
      acc.commissionAmount += Number(item.commissionAmount) || 0;
      acc.stateQty += Number(item.stateQty) || 0;
      acc.nfsaQty += Number(item.nfsaQty) || 0;
      acc.totalQty += Number(item.totalQty) || 0;
      acc.amount += Number(item.amount) || 0;
      acc.amountPayable += Number(item.amountPayable) || 0;
      acc.count += 1;
      return acc;
    },
    {
      riceQty: 0,
      commissionAmount: 0,
      stateQty: 0,
      nfsaQty: 0,
      totalQty: 0,
      amount: 0,
      amountPayable: 0,
      count: 0,
    }
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (verifyStateAbsCommission.length === 0) {
    return (
      <div className="mainhead" align="center">
        No data found
      </div>
    );
  }

  const handleDownloadExcel = async (data) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Verify State Commission ");

  const totalColumns = 9;

  // === Column Widths ===
  worksheet.columns = [
    { width: 6 },   // S.No
    { width: 22 },  // District
    { width: 15 },  // NFSA Qty
    { width: 22 },  // Amount Payable
    { width: 18 },  // State Qty
    { width: 18 },  // NFSA Add Qty
    { width: 18 },  // Total Qty
    { width: 18 },  // Amount
    { width: 22 },  // Total Amount Payable
  ];

  // === Row 1: Title ===
  worksheet.mergeCells(1, 1, 1, totalColumns);
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "Monthly FPS Dealer Commission Report for Verification";
  titleCell.font = { size: 12, bold: true };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };

  // === Row 2: First part of header ===
  worksheet.mergeCells("A2:A3");
  worksheet.getCell("A2").value = "S.No";

  worksheet.mergeCells("B2:B3");
  worksheet.getCell("B2").value = "District";

  worksheet.mergeCells("C2:C3");
  worksheet.getCell("C2").value = "NFSA Qty of Rice Distributed (A)";

  worksheet.mergeCells("D2:D3");
  worksheet.getCell("D2").value = `Amount Payable Towards Commission
(In Rs ${commissionData?.nfsaStateCost || 0} per kg)
(B=A*${commissionData?.nfsaStateCost || 0})`;

  // FIXED: Only merge 5 columns for State Commission
  worksheet.mergeCells("E2:I2");
  worksheet.getCell("E2").value = "State Commission";

  // === Row 3: Subheaders ===
  const subHeaders = [
    "State Qty of rice distributed (C)",
    "NFSA Add Qty of rice distributed (D)",
    "Total Qty (E=C+D)",
    `Amount (F=E*${commissionData?.stateCost || 0})`,
    "Total Amount Payable (G = B+F)",
  ];

  subHeaders.forEach((header, index) => {
    worksheet.getCell(3, 5 + index).value = header;
  });

  // === Header Styles (Row 2 & 3) ===
  [2, 3].forEach((rowNumber) => {
    const row = worksheet.getRow(rowNumber);
    row.font = { bold: true };
    row.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

    row.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDDDDDD" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
  });

  // === Add Data Rows ===
  data.forEach((item, index) => {
    const row = worksheet.addRow([
      index + 1,
      item.distNameEn,
      parseFloat(item.riceQty),
      parseFloat(item.commissionAmount),
      parseFloat(item.stateQty),
      parseFloat(item.nfsaQty),
      parseFloat(item.totalQty),
      parseFloat(item.amount),
      parseFloat(item.amountPayable),
    ]);

    // Formatting numeric columns
    row.getCell(3).numFmt = "0.000";
    row.getCell(4).numFmt = "0.00";
    row.getCell(5).numFmt = "0.000";
    row.getCell(6).numFmt = "0.000";
    row.getCell(7).numFmt = "0.000";
    row.getCell(8).numFmt = "0.00";
    row.getCell(9).numFmt = "0.00";

    // Alignments
    row.getCell(1).alignment = { horizontal: "left" };
    row.getCell(2).alignment = { horizontal: "left" };

    for (let i = 3; i <= 9; i++) {
      row.getCell(i).alignment = { horizontal: "right" };
    }
  });

  // === Total Row ===
  const totalRow = worksheet.addRow([
    "",
    "Total",
    parseFloat(totals.riceQty),
    parseFloat(totals.commissionAmount),
    parseFloat(totals.stateQty),
    parseFloat(totals.nfsaQty),
    parseFloat(totals.totalQty),
    parseFloat(totals.amount),
    parseFloat(totals.amountPayable),
  ]);

  totalRow.font = { bold: true };

  totalRow.getCell(3).numFmt = "0.000";
  totalRow.getCell(4).numFmt = "0.00";
  totalRow.getCell(5).numFmt = "0.000";
  totalRow.getCell(6).numFmt = "0.000";
  totalRow.getCell(7).numFmt = "0.000";
  totalRow.getCell(8).numFmt = "0.00";
  totalRow.getCell(9).numFmt = "0.00";

  totalRow.eachCell((cell, colNumber) => {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "double" },
      right: { style: "thin" },
    };
    if (colNumber <= 2) {
      cell.alignment = { horizontal: "left" };
    } else {
      cell.alignment = { horizontal: "right" };
    }
  });

  // === Save Excel File ===
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Verify_State_Wise_Commission.xlsx`);
};


  return (
    <>
      <div style={{ justifyContent: "center", marginBottom: "70px" }}>
        <table width="90%" border="1" cellPadding="5" cellSpacing="0">
          <thead>
            <tr className="tableheader">
              <th colSpan={14}>
                Monthly FPS Dealer Commission Report for Verification
              </th>
            </tr>
            <tr className="tableheader">
              <th rowSpan={2}>S.No</th>
              <th rowSpan={2}>District</th>
              <th rowSpan={2}>
                NFSA <br /> Qty of Rice Distributed{" "}
                <font style={{ color: "orange" }}>(A)</font>
              </th>
              {
                <th rowSpan={2}>
                  Amount Payable Towards Commission <br /> (In Rs
                  {commissionData.nfsaStateCost || 0} per kg) <br />{" "}
                  <font style={{ color: "orange" }}>
                    (B=A*{commissionData.nfsaStateCost || 0})
                  </font>
                </th>
              }
              <th colSpan={10}>State Commission</th>
            </tr>
            <tr className="tableheader">
              <th>
                State Qty of rice distributed <br />{" "}
                <font style={{ color: "orange" }}>(C)</font>
              </th>
              <th>
                NFSA Add Qty of rice distributed
                <br />
                <font style={{ color: "orange" }}>(D)</font>
              </th>
              <th>
                Total Qty
                <br />
                <font style={{ color: "orange" }}>(E=C+D)</font>
              </th>
              <th>
                Amount <br />
                <font style={{ color: "orange" }}>
                  (F=E*{commissionData.stateCost})
                </font>
              </th>
              <th>
                Total Amount Payable <br />
                <font style={{ color: "orange" }}>(G = B+F)</font>
              </th>
            </tr>
          </thead>
          <tbody>
            {verifyStateAbsCommission.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.distNameEn}</td>
                <td style={{ textAlign: "right" }}>
                  {item.riceQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.commissionAmount.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.stateQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.nfsaQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.totalQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>{item.amount.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>
                  {item.amountPayable.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold" }} className="tableheader">
              <td colSpan={2} style={{ textAlign: "center" }}>
                Total
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.riceQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {" "}
                {totals.commissionAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.stateQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.nfsaQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.totalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>{totals.amount.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>
                {totals.amountPayable.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          {
            <button
              onClick={() => handleDownloadExcel(verifyStateAbsCommission)}
              className="button button2"
            >
              Download Excel
            </button>
          }
        </div>
      </div>
    </>
  );
};

export default VerifyCommissionStateDetailsReport;
